import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useUpdatePatientEngagementMutation } from '../../api/patientsApi';
import { MARKETING_SOURCES } from './CollectPaymentModal';

const STATUS_LABEL = { NoShow: 'No Show', Cancelled: 'Cancelled' };

/**
 * First-touch marketing attribution for a patient's first appointment when it ends in No Show
 * or Cancelled — those never raise an invoice, so the Collect Payment modal's own attribution
 * section (gated on the first invoice) never gets a chance to ask. Saves onto the Patient record,
 * then `onSaved()` lets the caller retry the original status update.
 */
export default function FirstApptAttributionModal({ patientId, patientName, targetStatus, onClose, onSaved }) {
  const [marketingSource, setMarketingSource] = useState('');
  const [referralName, setReferralName] = useState('');
  const [save, { isLoading }] = useUpdatePatientEngagementMutation();

  const submit = async (e) => {
    e.preventDefault();
    if (!marketingSource) { toast.error('Select how the patient heard about the clinic.'); return; }
    if (marketingSource === 'Reference' && !referralName.trim()) { toast.error('Enter the referral name.'); return; }
    try {
      await save({
        id: patientId,
        marketingSource,
        referralName: marketingSource === 'Reference' ? referralName.trim() : null,
      }).unwrap();
      toast.success('Attribution recorded');
      onSaved?.();
    } catch (err) {
      toast.error(err?.data?.error?.message || 'Could not save the attribution.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header"><h2>How did they hear about us?</h2><button type="button" className="modal-close" onClick={onClose}><X size={18} /></button></div>
        <div className="modal-body">
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 0 }}>
            {patientName ? <strong>{patientName}</strong> : 'This patient'}'s first appointment is being marked{' '}
            <strong>{STATUS_LABEL[targetStatus] || targetStatus}</strong>. Record marketing attribution before continuing —
            this is the only chance to capture it since no bill will be raised.
          </p>
          <div className="form-group">
            <label className="form-label">Marketing source *</label>
            <select className="form-control" value={marketingSource} onChange={(e) => setMarketingSource(e.target.value)}>
              <option value="">— Select —</option>
              {MARKETING_SOURCES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {marketingSource === 'Reference' && (
            <div className="form-group">
              <label className="form-label">Referral name *</label>
              <input
                className="form-control"
                value={referralName}
                onChange={(e) => setReferralName(e.target.value)}
                placeholder="Who referred them?"
              />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>{isLoading ? 'Saving…' : 'Save & continue'}</button>
        </div>
      </form>
    </div>
  );
}
